// Single source of truth for the Year 2 dashboard taxonomy.
//
// Every metric is defined exactly once here as a descriptor. The dropdown
// structure, per-geo field lookups, value formatting ($/%/years/level),
// color-scale selection, legend titles, and suppression handling are all
// DERIVED from these descriptors — no display-name string is a join key.
// Renaming a `label` is render-only and cannot break behavior; renaming a
// data field fails `npm run type-check` here (fields are `keyof CountyData` /
// `keyof StateData`) instead of silently rendering N/A at runtime.
//
// IDs are the Y2 layout CSV `NAME` values (hvsourcedata/State_Layout_HS_y2.csv,
// county_layout_HS_y2.csv), so they are stable, unique across geo levels, and
// traceable to source. Declaration order IS dropdown / KPI-chip order.
// ============ Shortage categorical constants ============
// UPL_HV_u3_cat arrives as a CAPACITY quartile (code 0 = least capacity). The
// dashboard presents it as workforce SHORTAGE, which is the inverse: the least
// capacity is the worst shortage. These arrays stay indexed by the raw code, so
// they read down from "Very High" shortage at code 0.
export const CAPACITY_CATEGORY_LABELS = ['Very High', 'High', 'Low', 'Very Low'];
// One static color per shortage level, drawn from the site blue palette in
// dashboard.css. Darkest = worst shortage. THESE FOUR ARE THE TUNING KNOBS:
// edit a hex here and the map fill, legend swatch, and KPI chip all follow,
// since nothing else assigns a color to this metric.
const SHORTAGE_VERY_HIGH = '#0E3F70'; // deep navy   - worst shortage
const SHORTAGE_HIGH = '#006EAE'; // brand blue
const SHORTAGE_LOW = '#7FB8DA'; // mid blue
const SHORTAGE_VERY_LOW = '#C9DCEA'; // pale blue   - least shortage
// Indexed by the raw UPL_HV_u3_cat code, so this runs dark -> light to stay
// aligned with the labels above. Keep "Very Low" a clearly saturated blue: it
// sits next to the pale lavender MISSING_COLOR on the county map, and a
// near-white value here is hard to tell apart from a missing county.
export const CAPACITY_CATEGORY_COLORS = [
    SHORTAGE_VERY_HIGH,
    SHORTAGE_HIGH,
    SHORTAGE_LOW,
    SHORTAGE_VERY_LOW
];
// The same severities lower-cased, because the narrative uses them mid-sentence
// ("...a very high workforce shortage"). Derived so the two cannot drift apart.
export const CAPACITY_SHORTAGE_LABELS = CAPACITY_CATEGORY_LABELS.map(l => l.toLowerCase());
// ============ Value sentinels & sentinel colors ============
export const SUPPRESSED = '<5';
export const MISSING = 'Missing';
export const LOW_COLOR = '#BBD7E9'; // zero / <5 counties
export const MISSING_COLOR = '#E6E1F0'; // missing / suppressed (lavender)
// ============ Descriptor tables ============
const GROUPS = [
    { id: 'workforce', label: 'Workforce' },
    { id: 'demographics', label: 'Households' },
    { id: 'adjacent', label: 'Adjacent Fields' }
];
// Category definitions come from the "Definition" column of
// hvsourcedata/Detailed_template_v3.csv, keyed by category. The CSV repeats the
// same text at National/State/County, so one `description` covers every geo; the
// `descriptionByGeo.county` entries below are NOT from the CSV — they append the
// county map's suppression / missing-legend explanation to that base text.
// Long enough that repeating it verbatim in descriptionByGeo would be a drift
// hazard, so the county variant composes from it.
const CAPACITY_DESCRIPTION = 'View the approximate number of additional home visitors this area would need to reach ' +
    'approximately 25% of households with incomes below 200% of the federal poverty level ' +
    'with at least one child (under 3 or under 6 and not yet in kindergarten) or a pregnant ' +
    'woman, assuming a caseload of 20 households per home visitor. Higher numbers indicate ' +
    'greater shortage.';
const CATEGORIES = [
    {
        id: 'home-visitors', groupId: 'workforce', label: 'Home visitors and supervisors',
        description: 'View the estimated number of home visitors and supervisors.',
        //descriptionByGeo: {
        //    county: 'View the estimated number of home visitors and supervisors. A value of "missing" and lavender shading means an evidence-based home visiting agency is located in that county, but no count of home visitors is available. Values less than 5 are suppressed. A value of "N/A" means that no known evidence-based home visiting agency is located in that county.'
        //},
        //hasSuppressionNote: true,
        missingLegend: { label: 'Missing' }
    },
    //{
    //    id: 'capacity', groupId: 'workforce', label: 'Workforce shortage',
    //    description: CAPACITY_DESCRIPTION,
    //    //descriptionByGeo: {
    //    //    county: CAPACITY_DESCRIPTION + ' A value of "missing" and lavender shading means an evidence-based home visiting agency is located in that county, but insufficient data is available to calculate workforce shortage. A value of "N/A" means that no known evidence-based home visiting agency is located in that county.'
    //    //},
    //    //hasSuppressionNote: true,
    //    missingLegend: { label: 'Missing or<br />Suppressed' }
    //},
    {
        id: 'female', groupId: 'workforce', label: 'Female',
        description: 'View the percentage of home visitors who are female.'
    },
    {
        id: 'tenure', groupId: 'workforce', label: 'Work experience',
        headingLabel: 'Average work experience',
        description: 'View home visitors’ average years of experience in the home visiting field.'
    },
    {
        id: 'age', groupId: 'workforce', label: 'Average age',
        description: 'View home visitors’ average age.'
    },
    {
        id: 'education', groupId: 'workforce', label: 'Education level',
        description: 'View the highest education level completed by home visitors.'
    },
    {
        id: 'earnings', groupId: 'adjacent', label: 'Annual earnings',
        description: 'View average annual earnings in adjacent fields.'
    },
    {
        id: 'graduates', groupId: 'adjacent', label: 'Number of graduates',
        description: 'View the number of students who completed credentials or degrees in undergraduate programs adjacent to home visiting.'
    },
    {
        id: 'households', groupId: 'demographics', label: 'Households with children or a pregnant woman',
        description: 'View the estimated number of households with at least one child (under 3 or under 6 and not yet in kindergarten) or a pregnant woman, across income levels and for households with low incomes.'
    }
];
const METRICS = [
    // Home visitors and supervisors
    { id: 'total_HV', categoryId: 'home-visitors', label: 'Home Visitors', shortLabel: 'Home Visitors', stateField: 'total_HV', countyField: 'total_HV', unit: 'count', scale: 'log', suppression: 'hv' },
    { id: 'Super', categoryId: 'home-visitors', label: 'Supervisors', stateField: 'super', countyField: 'super', unit: 'count', scale: 'log', suppression: 'super' },
    // Workforce shortage - all four metrics from the Y2 state layout are live.
    //
    // The category mixes two opposite orderings, so read the scales carefully:
    //   - The Needcnt COUNTS are directly ordered (higher = worse shortage), so
    //     the log ramp is used as-is.
    //   - The per-1,000 RATES measure capacity (higher = MORE home visitors per
    //     1,000 households = LESS shortage), so they carry `invertScale` to keep
    //     "darkest = worst shortage" true across the whole dropdown.
    //
    // Declaration order is dropdown order, so the counts lead: they are the
    // headline figure and back the Workforce narrative's shortage sentence.
    { id: 'WF_Shortage_HV_Needcnt_u3', categoryId: 'capacity', label: 'Workforce shortage with a child under 3 or a pregnant woman', shortLabel: 'Workforce shortage with a child under 3 or a pregnant woman', stateField: 'wF_Shortage_HV_u3', countyField: 'wF_Shortage_HV_u3', unit: 'count', scale: 'log', legendTitle: 'HV needed', suppression: 'capacity' },
    { id: 'WF_Shortage_HV_Needcnt_u6', categoryId: 'capacity', label: 'Workforce shortage with a child under 6 or a pregnant woman', shortLabel: 'Workforce shortage with a child under 6 or a pregnant woman', stateField: 'wF_Shortage_HV_u6', countyField: 'wF_Shortage_HV_u6', unit: 'count', scale: 'log', legendTitle: 'HV needed', suppression: 'capacity' },
    //{ id: 'UPL_HV_u3_cat', categoryId: 'capacity', label: 'Workforce shortage', shortLabel: 'Shortage', labelByGeo: { county: 'Workforce shortage (categorical)' }, stateField: 'upL_HV_u3_cat', countyField: 'upL_HV_u3_cat', unit: 'category', scale: 'classes', legendTitle: 'Level', suppression: 'capacity' },
    { id: 'u200fpl_HV_per1000_u3', categoryId: 'capacity', label: 'Households per home visitor (with child under 3 or pregnant woman, low incomes)', shortLabel: 'Households per home visitor (with child under 3 or pregnant woman, low incomes)', stateField: 'u200fpl_HV_per1000_under3', countyField: 'u200fpl_HV_per1000_under3', unit: 'count', scale: 'log', invertScale: true, legendTitle: 'Shortage', suppression: 'capacity' },
    // The under-6 companion rate, carried at both geo levels (county index 10,
    // state index 39).
    { id: 'u200fpl_HV_per1000_u6', categoryId: 'capacity', label: 'Households per home visitor (with child under 6 or pregnant woman, low incomes)', shortLabel: 'Households per home visitor (with child under 6 or pregnant woman, low incomes)', stateField: 'u200fpl_HV_per1000_under6', countyField: 'u200fpl_HV_per1000_under6', unit: 'count', scale: 'log', invertScale: true, legendTitle: 'Shortage', suppression: 'capacity' },
    // Female (state only)
    { id: 'female', categoryId: 'female', label: 'Female', stateField: 'female', unit: 'percent', scale: 'percentile' },
    // Tenure (state only)
    // The average, in years — same shape as Age below: charting it beside the
    // five percent bands would add a sixth bar on a scale of its own.
    { id: 'tenure', categoryId: 'tenure', label: 'Years', stateField: 'tenure', unit: 'years', scale: 'percentile', excludeFromChart: true },
    { id: 'tenure_less_than_1_year', categoryId: 'tenure', label: 'Less than one year', stateField: 'tenure_less_than_1_year', unit: 'percent', scale: 'percentile' },
    { id: 'tenure_1_to_3_years', categoryId: 'tenure', label: '1-2 years', stateField: 'tenure_1_to_3_years', unit: 'percent', scale: 'percentile' },
    { id: 'tenure_3_to_5_years', categoryId: 'tenure', label: '3-4 years', stateField: 'tenure_3_to_5_years', unit: 'percent', scale: 'percentile' },
    { id: 'tenure_5_to_10_years', categoryId: 'tenure', label: '5-10 years', stateField: 'tenure_5_to_10_years', unit: 'percent', scale: 'percentile' },
    { id: 'tenure_more_than_10_years', categoryId: 'tenure', label: 'More than 10 years', stateField: 'tenure_more_than_10_years', unit: 'percent', scale: 'percentile' },
    // Age (state only)
    // The average, in years — charting it beside the six percent bands below
    // would add a seventh bar on a scale of its own. See `excludeFromChart`.
    { id: 'age', categoryId: 'age', label: 'Years', stateField: 'age', unit: 'years', decimals: 1, scale: 'percentile', excludeFromChart: true },
    { id: 'age_18_to_24', categoryId: 'age', label: '18-24 years', stateField: 'age_18_to_24', unit: 'percent', scale: 'percentile' },
    { id: 'age_25_to_29', categoryId: 'age', label: '25-29 years', stateField: 'age_25_to_29', unit: 'percent', scale: 'percentile' },
    { id: 'age_30_to_39', categoryId: 'age', label: '30-39 years', stateField: 'age_30_to_39', unit: 'percent', scale: 'percentile' },
    { id: 'age_40_to_49', categoryId: 'age', label: '40-49 years', stateField: 'age_40_to_49', unit: 'percent', scale: 'percentile' },
    { id: 'age_50_to_59', categoryId: 'age', label: '50-59 years', stateField: 'age_50_to_59', unit: 'percent', scale: 'percentile' },
    { id: 'age_60plus', categoryId: 'age', label: '60 years or older', stateField: 'age_60plus', unit: 'percent', scale: 'percentile' },
    // Education level (state only)
    { id: 'ed_HS', categoryId: 'education', label: 'High school or GED', shortLabel: 'High school or GED', stateField: 'ed_HS', unit: 'percent', scale: 'percentile' },
    { id: 'ed_AA', categoryId: 'education', label: 'Associate degree', shortLabel: 'Associate degree', stateField: 'ed_AA', unit: 'percent', scale: 'percentile' },
    { id: 'ed_BA', categoryId: 'education', label: "Bachelor's degree", shortLabel: "Bachelor's degree", stateField: 'ed_BA', unit: 'percent', scale: 'percentile' },
    { id: 'ed_MA', categoryId: 'education', label: 'Graduate degree', shortLabel: 'Graduate degree', stateField: 'ed_MA', unit: 'percent', scale: 'percentile' },
    { id: 'ed_BAMA', categoryId: 'education', label: "Bachelor's degree or higher", shortLabel: "Bachelor's degree or higher", stateField: 'ed_BAMA', unit: 'percent', scale: 'percentile' },
    // Annual earnings (state + county). Note ECE field casing differs by geo.
    { id: 'AnnualEarn_ECE', categoryId: 'earnings', label: 'Teacher, early education', shortLabel: 'Teacher, early education', shortChartLabel: 'Teacher, early<br />education', stateField: 'annualEarn_ece', countyField: 'annualEarn_ECE', unit: 'dollars', scale: 'percentile' },
    { id: 'AnnualEarn_HC', categoryId: 'earnings', label: 'Registered nurse', shortChartLabel: 'Registered nurse', stateField: 'annualEarn_HC', countyField: 'annualEarn_HC', unit: 'dollars', scale: 'percentile' },
    { id: 'AnnualEarn_SW', categoryId: 'earnings', label: 'Social worker', stateField: 'annualEarn_SW', countyField: 'annualEarn_SW', unit: 'dollars', scale: 'percentile' },
    { id: 'AnnualEarn_CHW', categoryId: 'earnings', label: 'Community health worker', shortLabel: 'Community health worker', shortChartLabel: 'Community<br />health worker', stateField: 'annualEarn_CHW', countyField: 'annualEarn_CHW', unit: 'dollars', scale: 'percentile' },
    // Number of graduates (state + county)
    { id: 'Graduates_ECE', categoryId: 'graduates', label: 'Early childhood and elementary education', shortLabel: 'Early childhood and elementary education', mobileLabel: 'Early childhood and<br/>elementary education', shortChartLabel: 'Early childhood and<br />elementary education', stateField: 'graduates_ECE', countyField: 'graduates_ECE', unit: 'count', scale: 'log', shareOfNational: true },
    { id: 'Graduates_HC', categoryId: 'graduates', label: 'Nursing', stateField: 'graduates_HC', countyField: 'graduates_HC', unit: 'count', scale: 'log', shareOfNational: true },
    { id: 'Graduates_SW', categoryId: 'graduates', label: 'Social work', stateField: 'graduates_SW', countyField: 'graduates_SW', unit: 'count', scale: 'log', shareOfNational: true },
    { id: 'Graduates_CHW', categoryId: 'graduates', label: 'Public health', stateField: 'graduates_CHW', countyField: 'graduates_CHW', unit: 'count', scale: 'log', shareOfNational: true },
    // Households (state + county)
    { id: 'pregWomen_or_childu6', categoryId: 'households', label: 'With a child under 6 or a pregnant woman', shortLabel: 'With a child under 6 or a pregnant woman', mobileLabel: 'With a child under 6<br />or a pregnant woman', shortChartLabel: 'Child under 6', stateField: 'pregWomen_or_childu6', countyField: 'pregWomen_or_childu6', unit: 'count', scale: 'log', legendTitle: 'Under 6 or pregnant woman', shareOfNational: true },
    { id: 'u200fpl_pregWomen_or_childu6', categoryId: 'households', label: 'With a child under 6 or a pregnant woman and low income', shortLabel: 'With a child under 6 or a pregnant woman and low income', mobileLabel: 'With a child under 6<br>or a pregnant woman<br>and low income', shortChartLabel: 'Child under 6<br />200% FPL', stateField: 'u200fpl_pregWomen_or_childu6', countyField: 'u200fpl_pregWomen_or_childu6', unit: 'count', scale: 'log', legendTitle: 'Under 6 or pregnant woman (low income)', shareOfNational: true },
    { id: 'pregWomen_or_childu3', categoryId: 'households', label: 'With a child under 3 or a pregnant woman', shortLabel: 'With a child under 3 or a pregnant woman', mobileLabel: 'With a child under 3<br />or a pregnant woman', shortChartLabel: 'Child under 3', stateField: 'pregWomen_or_childu3', countyField: 'pregWomen_or_childu3', unit: 'count', scale: 'log', legendTitle: 'Under 3 or pregnant woman', shareOfNational: true },
    { id: 'u200fpl_pregWomen_or_childu3', categoryId: 'households', label: 'With a child under 3 or a pregnant woman and low income', shortLabel: 'With a child under 3 or a pregnant woman and low income', mobileLabel: 'With a child under 3<br>or a pregnant woman<br>and low income', shortChartLabel: 'Child under 3 <br />200% FPL', stateField: 'u200fpl_pregWomen_or_childu3', countyField: 'u200fpl_pregWomen_or_childu3', unit: 'count', scale: 'log', legendTitle: 'Under 3 or pregnant woman (low income)', shareOfNational: true }
];
// ============ Derived lookups (built once at module load) ============
const groupById = new Map(GROUPS.map(g => [g.id, g]));
const categoryById = new Map(CATEGORIES.map(c => [c.id, c]));
const metricById = new Map(METRICS.map(m => [m.id, m]));
function metricAvailable(m, geo) {
    if (m.hidden)
        return false;
    return geo === 'state' ? m.stateField != null : m.countyField != null;
}
function categoryAvailable(c, geo) {
    return METRICS.some(m => m.categoryId === c.id && metricAvailable(m, geo));
}
// ============ Public accessors ============
export function getGroup(id) {
    return id ? groupById.get(id) : undefined;
}
export function getCategory(id) {
    return id ? categoryById.get(id) : undefined;
}
export function getMetric(id) {
    return id ? metricById.get(id) : undefined;
}
/**
 * Category text for panel headings, which have room for wording the dropdown
 * does not. Falls back to the dropdown label, so a category only needs
 * headingLabel when the two genuinely differ.
 */
export function getCategoryHeading(id) {
    var _a;
    const category = getCategory(id);
    return category ? (_a = category.headingLabel) !== null && _a !== void 0 ? _a : category.label : '';
}
export function getGroups(geo) {
    return GROUPS.filter(g => CATEGORIES.some(c => c.groupId === g.id && categoryAvailable(c, geo)));
}
export function getCategories(groupId, geo) {
    return CATEGORIES.filter(c => c.groupId === groupId && categoryAvailable(c, geo));
}
export function getMetrics(categoryId, geo) {
    return METRICS.filter(m => m.categoryId === categoryId && metricAvailable(m, geo));
}
export function getFirstMetricId(categoryId, geo) {
    const metrics = getMetrics(categoryId, geo);
    return metrics.length > 0 ? metrics[0].id : null;
}
/** Metric display label, honoring geo-specific overrides. */
export function getMetricLabel(id, geo) {
    var _a;
    const d = getMetric(id);
    if (!d)
        return '';
    return (_a = d.shortLabel) !== null && _a !== void 0 ? _a : d.label;
}
export function getMetricMobileLabel(id, geo) {
    var _a;
    const d = getMetric(id);
    if (!d)
        return '';
    return (_a = d.mobileLabel) !== null && _a !== void 0 ? _a : d.label;
}
export function getCategoryDescription(id, geo) {
    var _a, _b;
    const c = getCategory(id);
    if (!c)
        return null;
    return (_b = (_a = c.descriptionByGeo) === null || _a === void 0 ? void 0 : _a[geo]) !== null && _b !== void 0 ? _b : c.description;
}
/** True for the categorical (level) shortage metric. */
export function isCategorical(id) {
    var _a;
    return ((_a = getMetric(id)) === null || _a === void 0 ? void 0 : _a.unit) === 'category';
}
export function getScale(id) {
    var _a, _b;
    return (_b = (_a = getMetric(id)) === null || _a === void 0 ? void 0 : _a.scale) !== null && _b !== void 0 ? _b : 'log';
}
/**
 * True when the color ramp must be flipped so darkest reads as "worst" — see
 * MetricDescriptor.invertScale. Color only; the displayed value is untouched.
 */
export function shouldInvertScale(id) {
    var _a;
    return !!((_a = getMetric(id)) === null || _a === void 0 ? void 0 : _a.invertScale);
}
/** Shortened legend title; falls back to the metric label for the geo. */
export function getLegendTitle(id, geo) {
    var _a;
    const d = getMetric(id);
    if (!d)
        return '';
    return (_a = d.legendTitle) !== null && _a !== void 0 ? _a : getMetricLabel(id, geo);
}
/** The county-only "Missing"/"Missing or Suppressed" legend swatch config. */
export function getMissingLegend(categoryId, geo) {
    var _a, _b;
    const c = getCategory(categoryId);
    return {
        label: (_b = (_a = c === null || c === void 0 ? void 0 : c.missingLegend) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : 'Missing',
        show: geo === 'county' && !!(c === null || c === void 0 ? void 0 : c.missingLegend)
    };
}
export function hasSuppressionNote(categoryId) {
    var _a;
    return !!((_a = getCategory(categoryId)) === null || _a === void 0 ? void 0 : _a.hasSuppressionNote);
}
// ============ Value reads (raw; sentinels applied) ============
/**
 * Read a raw county value for a metric: the numeric field value, a suppression
 * sentinel ("<5" / "Missing"), or null. The categorical shortage metric returns
 * its raw 0-3 code (the display label is applied by formatMetricValue).
 */
export function readCountyValue(row, id) {
    const d = getMetric(id);
    if (!d || !d.countyField)
        return null;
    if (d.suppression === 'hv') {
        if (row.isTotalHVSuppressed === true)
            return SUPPRESSED;
        if (row.isTotalHVMissing === true)
            return MISSING;
    }
    else if (d.suppression === 'super') {
        if (row.isTotalSuperSuppressed === true)
            return SUPPRESSED;
        if (row.isTotalSuperMissing === true)
            return MISSING;
    }
    else if (d.suppression === 'capacity') {
        if (row.isSupplyDemandMissing === true)
            return MISSING;
    }
    return row[d.countyField];
}
/** Read a raw state value for a metric (state data carries no suppression). */
export function readStateValue(row, id) {
    const d = getMetric(id);
    if (!d || !d.stateField)
        return null;
    return row[d.stateField];
}
// ============ Formatting ============
/**
 * Convert a UPL_HV_u3_cat quartile code (0-3) to its displayed shortage level
 * ("Very High" ... "Very Low"). Note the raw code counts capacity, so code 0 is
 * the *worst* shortage — see CAPACITY_CATEGORY_LABELS.
 */
export function getCapacityCategoryLabel(code) {
    var _a;
    if (code == null || !Number.isInteger(code))
        return null;
    return (_a = CAPACITY_CATEGORY_LABELS[code]) !== null && _a !== void 0 ? _a : null;
}
/**
 * Same shortage severity lower-cased for prose ("This indicates a very high
 * workforce shortage") — see CAPACITY_SHORTAGE_LABELS.
 */
export function getCapacityShortageLabel(code) {
    var _a;
    if (code == null || !Number.isInteger(code))
        return null;
    return (_a = CAPACITY_SHORTAGE_LABELS[code]) !== null && _a !== void 0 ? _a : null;
}
/**
 * The single value formatter for KPI, tooltip, and legend. Applies the metric's
 * unit ($/%/level/plain), fixed decimals, and — for the legend — compact K/M
 * rounding. Suppression sentinels pass through unchanged.
 */
export function formatMetricValue(id, value, opts) {
    var _a;
    if (value === null || value === undefined)
        return 'N/A';
    if (typeof value === 'string') {
        // Suppression sentinels (and any other string) pass through as-is.
        return value;
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return 'N/A';
    }
    const d = getMetric(id);
    if ((d === null || d === void 0 ? void 0 : d.unit) === 'category') {
        return (_a = getCapacityCategoryLabel(value)) !== null && _a !== void 0 ? _a : 'N/A';
    }
    let num;
    if (opts === null || opts === void 0 ? void 0 : opts.compact) {
        const abs = Math.abs(value);
        if (abs >= 1000000)
            num = Math.round(value / 1000000) + 'M';
        else if (abs >= 1000)
            num = Math.round(value / 1000) + 'K';
        else
            num = Math.round(value).toString();
    }
    else if ((d === null || d === void 0 ? void 0 : d.decimals) != null) {
        num = value.toFixed(d.decimals);
    }
    else {
        num = value.toLocaleString();
    }
    const prefix = (d === null || d === void 0 ? void 0 : d.unit) === 'dollars' ? '$' : '';
    const suffix = (d === null || d === void 0 ? void 0 : d.unit) === 'percent' ? '%' : '';
    return `${prefix}${num}${suffix}`;
}
